import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import React from 'react';
import useFetch from 'e3-common/hooks/useFetch';
import { validate } from 'e3-common/utils/validationUtil';
import { useBoundStore } from 'e3-common/store/useBoundStore';
import formStyles from '@/css/FormSection.module.css';
import { E3ModalPopup } from 'e3-components';
import { FormSection } from 'e3-components';
import { LOCAL_STORAGE_KEYS } from 'e3-common/constants/enumerates';
import {setLocalStorageItem, setSessionStorageItem} from 'e3-common/utils/localStorageUtil';
import useMessageBox from 'e3-common/hooks/useMessageBox';
import { clear } from 'e3-common/utils/controlsUtil';
import useAuth from 'e3-common/hooks/useAuth';
const PWChangePopup = React.memo(forwardRef((props, ref) => {
    const { request } = useFetch();
    const {userId} = useBoundStore((state) => state.user);
    const { alert, confirm } = useMessageBox();
    const { logout } = useAuth();

    useImperativeHandle(ref, () => ({
        showPopup: () => {
            clear(controls.form);
            controls.refPopup.current.showPopup();
        },
    }));

    const controls = {
        refPopup: useRef(),
        form: {
            txtOriginPassword: {
                ref: useRef(),
                label: '기존 비밀번호',
                labelWidth: 'wpx_180',
                validation: { required: true },
                controlOption: {
                    type: 'password',
                    placeholder: '기존 비밀번호를 입력하세요.',
                },
            },
            txtNewPassword: {
                ref: useRef(),
                label: '신규 비밀번호',
                validation: { required: true },
                controlOption: {
                    type: 'password',
                    placeholder: '신규 비밀번호를 입력하세요.',
                },
            },
            txtChkPassword: {
                ref: useRef(),
                label: '신규 비밀번호 확인',
                validation: { required: true },
                controlOption: {
                    type: 'password',
                    placeholder: '신규 비밀번호를 다시 입력하세요.',
                },
            },
        },
        buttons: {
            btnPWChange: {
                checkAuth : false,
                label: '비밀번호변경',
                onClick: () => {
                    pageFunctions.pwChange();
                },
            },
        },
    };

    const popupFunctions = {
        validatePW: async () => {
            const newPW = controls.form.txtNewPassword.ref;
            const chkPW = controls.form.txtChkPassword.ref;

            if (validate(controls.form)) {
                if (newPW.current?.getValue() !== chkPW.current?.getValue()) {
                    alert('', '비밀번호 확인이 일치하지 않습니다.');
                    throw new Error('비밀번호 확인이 일치하지 않습니다.');
                }
            }
        },
        validChk: async () => {
            const chkPW = controls.form.txtChkPassword.ref.current?.getValue();

            if (chkPW.length < 10) {
                alert('', '비밀번호는 10자 이상입니다.');
                throw new Error('비밀번호는 10자 이상입니다.');
            } else if (chkPW.length > 30) {
                alert('', '비밀번호는 30자 이하입니다.');
                throw new Error('비밀번호는 30자 이하입니다.');
            }

            if ((chkPW.match(/[\p{Script=Hangul}]/u) || []).length) {
                alert('', '비밀번호에 한글은 사용할 수 없습니다.');
                throw new Error('비밀번호에 한글은 사용할 수 없습니다.');
            } else {
                const chkItem = {
                    digit: (chkPW.match(/\d/g) || []).length,
                    lower: (chkPW.match(/[a-z]/g) || []).length,
                    upper: (chkPW.match(/[A-Z]/g) || []).length,
                    special: (
                        chkPW.match(
                            /[\!\@\#\$\%\^\&\*\(\)\-\_\=\~\:\;\<\>\.\,\{\}\[\]\\\|\'\`\?]/g
                        ) || []
                    ).length,
                };

                let cnt = 0;
                for (let i in chkItem) {
                    if (chkItem[i] > 0) cnt++;
                }

                if (cnt < 3) {
                    alert(
                        '',
                        '비밀번호는 대문자, 소문자, 특수문자, 숫자 중 3가지 이상 조합해야 합니다.'
                    );
                    throw new Error(
                        '비밀번호는 대문자, 소문자, 특수문자, 숫자 중 3가지 이상 조합해야 합니다.'
                    );
                }
            }
        },
        commonValidChk: async () => {
            const chkPW = controls.form.txtChkPassword.ref.current?.getValue();
            const regex = /^[0-9]+$/;

            if (chkPW.length < 6) {
                alert('', '비밀번호는 6자 이상입니다.');
                throw new Error('비밀번호는 6자 이상입니다.');
            }

            if (!regex.test(chkPW)) {
                alert('', '비밀번호는 숫자만 가능합니다.');
                throw new Error('비밀번호는 숫자만 가능합니다.');
            }
        },
        pwChange: async () => {
            const confirmResult = await confirm('confirm', '비밀번호를 변경하시겠습니까?');

            if (confirmResult) {
                try {
                    await popupFunctions.validatePW();
                    await popupFunctions.validChk();

                    const requestOptions = {
                        method: 'POST',
                        body: {
                            USER_ID: userId,
                            PASSWORD: controls.form.txtOriginPassword.ref.current?.getValue(),
                            NEW_PASSWORD: controls.form.txtNewPassword.ref.current?.getValue(),
                        },
                    };
                    const messageOptions = {
                        showAlert: true,
                        alertTitle: '오류',
                        showSuccess: false,
                    };
                    const response = await request(
                        // '/user/password-change',
                        '/E3/SYSTEM/ChangePassword',
                        requestOptions,
                        messageOptions
                    );
                    if (response) {
                        setSessionStorageItem(
                            LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
                            response.USER.ACCESS_TOKEN
                        );
                        await popupFunctions.initForm();
                    }
                } catch (e) {
                    alert('', e.message);
                }
            }
        },
        initForm: async () => {
            await clear(controls.form, true);
            await controls.refPopup.current.closePopup();
            await logout(userId);
        },
    };
    return (
        <E3ModalPopup
            ref={controls.refPopup}
            defaultTitle={'비밀번호 변경'}
            confirmBtnLabel="비밀번호 변경"
            confirmBtnEvent={popupFunctions.pwChange}
            defaultHeight="400px"
            defaultWidth="550px"
            closeBtnEvent = {() => {}}
            endEvent={() => clear(controls.form)}
        >
            <FormSection formControls={controls.form} numOfCols={1} />
            <div className={formStyles.description}>
                <label style={{ color: 'red' }}>대문자, 소문자, 특수문자, 숫자</label> 중 3가지 이상
                조합하여 10자 이상을 사용하세요
            </div>
        </E3ModalPopup>
    );
}));

PWChangePopup.displayName = 'PWChangePopup';
export default PWChangePopup;
